import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import walletService from '../services/walletService';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [walletData, setWalletData] = useState(null);
  const [walletStats, setWalletStats] = useState(null);
  const [walletLimits, setWalletLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTransactionLogs, setShowTransactionLogs] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [transactionError, setTransactionError] = useState(null);

  const handleLogout = () => {
    logout();
  };



  const goToProfile = () => {
    navigate('/profile');
  };

  const goToTournaments = () => {
    navigate('/tournaments');
  };

  // Load wallet data on component mount
  useEffect(() => {
    const loadWalletData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Test if wallet service is available first
        const testResult = await walletService.getBalance().catch(err => {
          if (err.response?.status === 401 || err.response?.status === 404) {
            // Service not available or not authenticated for wallet - use fallback
            console.info('Wallet service not available - showing demo data');
            return { success: false, demo: true };
          }
          throw err;
        });

        if (testResult.demo) {
          // Show demo/fallback data
          setError('Wallet service setup in progress - showing demo data');
          setWalletData({
            balance: 0,
            currency: 'USD',
            status: 'pending_setup',
            lastReconciled: new Date().toISOString()
          });
          setWalletStats({
            totalIncome: 0,
            totalExpenses: 0,
            transactionCount: 0,
            averageTransaction: 0
          });
          setWalletLimits({
            dailyLimit: 1000,
            monthlyLimit: 5000,
            dailyUsed: 0,
            monthlyUsed: 0
          });
          return;
        }

        // If we get here, wallet service is available
        const [balanceResult, statsResult, limitsResult] = await Promise.allSettled([
          walletService.getBalance(),
          walletService.getStatistics(),
          walletService.getLimits()
        ]);

        if (balanceResult.status === 'fulfilled' && balanceResult.value?.success !== false) {
          setWalletData(balanceResult.value.data);
        }
        if (statsResult.status === 'fulfilled' && statsResult.value?.success !== false) {
          setWalletStats(statsResult.value.data);
        }
        if (limitsResult.status === 'fulfilled' && limitsResult.value?.success !== false) {
          setWalletLimits(limitsResult.value.data);
        }
      } catch (err) {
        console.error('Error loading wallet data:', err);
        setError('Unable to connect to wallet services');
      } finally {
        setLoading(false);
      }
    };

    loadWalletData();
  }, []);

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  // Function to load transaction history
  const loadTransactionHistory = async () => {
    try {
      setTransactionLoading(true);
      setTransactionError(null);

      const result = await walletService.getTransactions({
        page: 1,
        limit: 50, // Show more transactions in the modal
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (result.success && result.data) {
        setTransactions(result.data.transactions);
      }
    } catch (error) {
      console.error('Error loading transaction history:', error);
      setTransactionError('Failed to load transaction history');
    } finally {
      setTransactionLoading(false);
    }
  };

  // Handle opening transaction logs modal
  const handleViewLogs = () => {
    setShowTransactionLogs(true);
    loadTransactionHistory();
  };

  // Function to format transaction type for display
  const formatTransactionType = (type) => {
    const typeMap = {
      DEPOSIT: 'Deposit',
      WITHDRAWAL: 'Withdrawal',
      BET: 'Bet Placed',
      WIN: 'Prize Won',
      REFUND: 'Refund',
      TRANSFER_IN: 'Transfer In',
      TRANSFER_OUT: 'Transfer Out'
    };
    return typeMap[type] || type;
  };

  // Function to get transaction color based on type
  const getTransactionColor = (type, amount) => {
    const creditTypes = ['DEPOSIT', 'WIN', 'REFUND', 'TRANSFER_IN'];
    const debitTypes = ['WITHDRAWAL', 'BET', 'TRANSFER_OUT'];
    
    if (creditTypes.includes(type)) {
      return 'var(--success)'; // Green for credits
    } else if (debitTypes.includes(type)) {
      return 'var(--error)'; // Red for debits
    }
    return 'var(--text-muted)'; // Gray for unknown
  };

  // Function to calculate total from transactions (for verification)
  const calculateTransactionTotal = (transactions) => {
    const creditTypes = ['DEPOSIT', 'WIN', 'REFUND', 'TRANSFER_IN'];
    const debitTypes = ['WITHDRAWAL', 'BET', 'TRANSFER_OUT'];
    
    let total = 0;
    transactions.forEach(tx => {
      const amount = parseFloat(tx.amount);
      if (creditTypes.includes(tx.type)) {
        total += amount;
      } else if (debitTypes.includes(tx.type)) {
        total -= amount;
      }
    });
    return total;
  };

  return (
    <div className="proskins-content" style={{
      padding: 'var(--spacing-lg)',
      // maxWidth: '1200px',
      margin: '0 auto',
      minHeight: '100vh'
    }}>
      <div style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <h1 className="proskins-section-title" style={{ 
          marginBottom: 'var(--spacing-sm)',
          color: 'var(--proskins-green)',
          fontSize: 'var(--font-size-3xl)',
          fontWeight: '700'
        }}>
          DASHBOARD
        </h1>
        <p className="proskins-text-secondary" style={{ fontSize: 'var(--font-size-lg)' }}>
          Welcome back, {user?.firstName}! This is your simplified dashboard.
        </p>
      </div>

      <div className="dashboard-grid-4-col">
        {/* User Info Card */}
        <div className="proskins-card">
          <h3 className="proskins-card-title" style={{ 
            marginBottom: 'var(--spacing-lg)',
            color: 'var(--proskins-green)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: '600'
          }}>Your Account</h3>
          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <p className="proskins-text-secondary" style={{ marginBottom: 'var(--spacing-sm)' }}>
              <span className="proskins-text-primary" style={{ fontWeight: '600' }}>Name:</span> {user?.firstName} {user?.lastName}
            </p>
            <p className="proskins-text-secondary" style={{ marginBottom: 'var(--spacing-sm)' }}>
              <span className="proskins-text-primary" style={{ fontWeight: '600' }}>Email:</span> {user?.email}
            </p>
            <p className="proskins-text-secondary" style={{ marginBottom: 'var(--spacing-sm)' }}>
              <span className="proskins-text-primary" style={{ fontWeight: '600' }}>Role:</span> {user?.role}
            </p>
            <p className="proskins-text-secondary">
              <span className="proskins-text-primary" style={{ fontWeight: '600' }}>Status:</span> {user?.isVerified ? '✅ Verified' : '❌ Not Verified'}
            </p>
          </div>
          {/* <button
            className="proskins-btn proskins-btn-outline proskins-btn-full"
            onClick={goToProfile}
          >
            Edit Profile
          </button> */}
        </div>

        {/* Quick Actions Card */}
        <div className="proskins-card">
          <h3 className="proskins-card-title" style={{ 
            marginBottom: 'var(--spacing-lg)',
            color: 'var(--proskins-green)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: '600'
          }}>Quick Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            {user?.role === 'ADMIN' && (
              <button
                className="proskins-btn proskins-btn-primary proskins-btn-full"
                onClick={() => navigate('/admin')}
                style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  border: 'none',
                  fontWeight: '600',
                  boxShadow: '0 2px 8px rgba(240, 147, 251, 0.3)',
                  marginBottom: 'var(--spacing-xs)'
                }}
              >
                🛠️ Admin Panel
              </button>
            )}
            <button
              className="proskins-btn proskins-btn-primary proskins-btn-full"
              onClick={goToTournaments}
            >
              🏌️ View Tournaments
            </button>

            <button
              className="proskins-btn proskins-btn-outline proskins-btn-full"
              onClick={handleLogout}
            >
              🚪 Logout
            </button>
          </div>
        </div>

        {/* Stats Card */}
        <div className="proskins-card">
          <h3 className="proskins-card-title" style={{ 
            marginBottom: 'var(--spacing-lg)',
            color: 'var(--proskins-green)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: '600'
          }}>Account Stats</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: 'var(--spacing-sm)',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-primary)'
            }}>
              <span className="proskins-text-secondary">Member Since:</span>
              <strong className="proskins-text-primary">{new Date(user?.createdAt).toLocaleDateString()}</strong>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: 'var(--spacing-sm)',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-primary)'
            }}>
              <span className="proskins-text-secondary">Account Type:</span>
              <strong className="proskins-text-primary">{user?.role}</strong>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: 'var(--spacing-sm)',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-primary)'
            }}>
              <span className="proskins-text-secondary">Last Login:</span>
              <strong className="proskins-text-primary">Just now</strong>
            </div>
          </div>
        </div>

        {/* Wallet Balance Card */}
        <div className="proskins-card">
          <h3 className="proskins-card-title" style={{ 
            marginBottom: 'var(--spacing-lg)',
            color: 'var(--proskins-green)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: '600'
          }}>💰 Wallet Balance</h3>
          {loading ? (
            <div className="proskins-text-center proskins-text-muted" style={{ padding: 'var(--spacing-xl)' }}>
              <div className="proskins-loading" style={{ marginBottom: 'var(--spacing-sm)' }}></div>
              Loading wallet data...
            </div>
          ) : error ? (
            <div className="proskins-alert proskins-alert-error proskins-text-center">
              {error}
            </div>
          ) : walletData ? (
            <div>
              <div style={{
                fontSize: 'var(--font-size-4xl)',
                fontWeight: 'bold',
                color: 'var(--proskins-green)',
                marginBottom: 'var(--spacing-sm)',
                textAlign: 'center'
              }}>
                {formatCurrency(walletData.balance, walletData.currency)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="proskins-text-secondary">Status:</span>
                  <span style={{ 
                    color: walletData.status === 'active' ? 'var(--success)' : 
                           walletData.status === 'pending_setup' ? 'var(--warning)' : 'var(--error)',
                    textTransform: 'capitalize'
                  }}>
                    {walletData.status === 'pending_setup' ? 'Setup Pending' : walletData.status}
                  </span>
                </div>
                {walletData.lastReconciled && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="proskins-text-secondary">Last Updated:</span>
                    <span className="proskins-text-primary">{new Date(walletData.lastReconciled).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 'var(--spacing-lg)', textAlign: 'center' }}>
                <button
                  className="proskins-btn proskins-btn-outline"
                  onClick={handleViewLogs}
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    padding: 'var(--spacing-sm) var(--spacing-md)'
                  }}
                >
                  📋 View Transaction Logs
                </button>
              </div>
            </div>
          ) : (
            <div className="proskins-text-center proskins-text-muted" style={{ padding: 'var(--spacing-xl)' }}>
              No wallet data available
            </div>
          )}
        </div>

        {/* Wallet Statistics Card */}
        <div className="proskins-card">
          <h3 className="proskins-card-title" style={{ 
            marginBottom: 'var(--spacing-lg)',
            color: 'var(--proskins-green)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: '600'
          }}>📊 Monthly Stats</h3>
          {walletStats ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: 'var(--spacing-sm)',
                background: 'rgba(0, 255, 136, 0.1)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid rgba(0, 255, 136, 0.3)'
              }}>
                <span className="proskins-text-secondary">Total Income:</span>
                <strong style={{ color: 'var(--success)' }}>
                  {formatCurrency(0)}
                </strong>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: 'var(--spacing-sm)',
                background: 'rgba(255, 68, 68, 0.1)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid rgba(255, 68, 68, 0.3)'
              }}>
                <span className="proskins-text-secondary">Total Expenses:</span>
                <strong style={{ color: 'var(--error)' }}>
                  {formatCurrency(walletStats.totalExpenses || 0)}
                </strong>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: 'var(--spacing-sm)',
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-primary)'
              }}>
                <span className="proskins-text-secondary">Transactions:</span>
                <strong className="proskins-text-primary">{walletStats.transactionCount || 0}</strong>
              </div>
              {walletStats.averageTransaction && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: 'var(--spacing-sm)',
                  background: 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-primary)'
                }}>
                  <span className="proskins-text-secondary">Avg Transaction:</span>
                  <strong className="proskins-text-primary">{formatCurrency(walletStats.averageTransaction)}</strong>
                </div>
              )}
            </div>
          ) : (
            <div className="proskins-text-center proskins-text-muted" style={{ padding: 'var(--spacing-xl)' }}>
              {loading ? 'Loading stats...' : 'No statistics available'}
            </div>
          )}
        </div>

        {/* Wallet Limits Card */}
        {/* {walletLimits && (
          <div className="proskins-card">
            <h3 className="proskins-card-title" style={{ 
              marginBottom: 'var(--spacing-lg)',
              color: 'var(--proskins-green)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: '600'
            }}>💳 Account Limits</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
              {walletLimits.dailyLimit && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacing-xs)' }}>
                    <span className="proskins-text-secondary">Daily Limit:</span>
                    <span className="proskins-text-primary">{formatCurrency(walletLimits.dailyLimit)}</span>
                  </div>
                  {walletLimits.dailyUsed !== undefined && (
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderRadius: 'var(--radius-sm)',
                      overflow: 'hidden',
                      border: '1px solid var(--border-primary)'
                    }}>
                      <div style={{
                        width: `${Math.min((walletLimits.dailyUsed / walletLimits.dailyLimit) * 100, 100)}%`,
                        height: '100%',
                        backgroundColor: walletLimits.dailyUsed > walletLimits.dailyLimit * 0.8 ? 'var(--error)' : 'var(--success)',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  )}
                </div>
              )}
              {walletLimits.monthlyLimit && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacing-xs)' }}>
                    <span className="proskins-text-secondary">Monthly Limit:</span>
                    <span className="proskins-text-primary">{formatCurrency(walletLimits.monthlyLimit)}</span>
                  </div>
                  {walletLimits.monthlyUsed !== undefined && (
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderRadius: 'var(--radius-sm)',
                      overflow: 'hidden',
                      border: '1px solid var(--border-primary)'
                    }}>
                      <div style={{
                        width: `${Math.min((walletLimits.monthlyUsed / walletLimits.monthlyLimit) * 100, 100)}%`,
                        height: '100%',
                        backgroundColor: walletLimits.monthlyUsed > walletLimits.monthlyLimit * 0.8 ? 'var(--error)' : 'var(--success)',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )} */}

        {/* Features Card */}
        {/* <div className="proskins-card">
          <h3 className="proskins-card-title" style={{ 
            marginBottom: 'var(--spacing-lg)',
            color: 'var(--proskins-green)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: '600'
          }}>Available Features</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
            <div className="proskins-text-secondary">✅ User Profile Management</div>
            <div className="proskins-text-secondary">✅ Tournament Viewing</div>
            <div className="proskins-text-secondary">✅ Account Settings</div>
            <div style={{ color: 'var(--success)' }}>✅ Wallet Management</div>
            <div className="proskins-text-muted">🔒 Live Betting (Coming Soon)</div>
            <div className="proskins-text-muted">🔒 Advanced Reports (Coming Soon)</div>
          </div>
        </div> */}
      </div>

      {/* Transaction Logs Modal */}
      {showTransactionLogs && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: 'var(--spacing-lg)'
        }}>
          <div className="proskins-card" style={{
            maxWidth: '900px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: 'var(--shadow-lg)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-lg)' }}>
              <h2 className="proskins-card-title" style={{ margin: 0 }}>📋 Transaction History</h2>
              <button
                onClick={() => setShowTransactionLogs(false)}
                className="proskins-btn proskins-btn-secondary"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 'var(--font-size-xl)',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: 'var(--spacing-xs)',
                  borderRadius: 'var(--radius-sm)'
                }}
              >
                ×
              </button>
            </div>

            {walletData && (
              <div style={{
                padding: 'var(--spacing-md)',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--spacing-lg)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid var(--border-primary)'
              }}>
                <div>
                  <strong className="proskins-text-primary">Current Balance: {formatCurrency(walletData.balance, walletData.currency)}</strong>
                </div>
                {transactions.length > 0 && (
                  <div className="proskins-text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                    Showing {transactions.length} recent transactions
                  </div>
                )}
              </div>
            )}

            {transactionLoading ? (
              <div className="proskins-text-center proskins-text-muted" style={{ padding: 'var(--spacing-2xl)' }}>
                <div className="proskins-loading" style={{ marginBottom: 'var(--spacing-sm)' }}></div>
                Loading transaction history...
              </div>
            ) : transactionError ? (
              <div className="proskins-alert proskins-alert-error proskins-text-center">
                {transactionError}
              </div>
            ) : transactions.length === 0 ? (
              <div className="proskins-text-center proskins-text-muted" style={{ padding: 'var(--spacing-2xl)' }}>
                No transactions found
              </div>
            ) : (
              <div>
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {transactions.map((tx, index) => (
                    <div
                      key={tx.id}
                      style={{
                        padding: 'var(--spacing-md)',
                        borderBottom: '1px solid var(--border-primary)',
                        backgroundColor: index % 2 === 0 ? 'var(--bg-tertiary)' : 'var(--bg-card)',
                        borderRadius: 'var(--radius-md)',
                        marginBottom: 'var(--spacing-xs)',
                        border: '1px solid var(--border-primary)'
                      }}
                    >
                      {/* Transaction Header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 'var(--spacing-sm)',
                        flexWrap: 'wrap',
                        gap: 'var(--spacing-sm)'
                      }}>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                          <div style={{
                            color: getTransactionColor(tx.type),
                            fontWeight: 'bold',
                            fontSize: 'var(--font-size-base)',
                            marginBottom: 'var(--spacing-xs)'
                          }}>
                            {formatTransactionType(tx.type)}
                          </div>
                          <div className="proskins-text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                            {new Date(tx.createdAt).toLocaleDateString()} at {new Date(tx.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            color: getTransactionColor(tx.type),
                            fontWeight: 'bold',
                            fontSize: 'var(--font-size-lg)',
                            marginBottom: 'var(--spacing-xs)'
                          }}>
                            {['DEPOSIT', 'WIN', 'REFUND', 'TRANSFER_IN'].includes(tx.type) ? '+' : '-'}
                            {formatCurrency(tx.amount)}
                          </div>
                          <div className="proskins-text-secondary" style={{ 
                            fontSize: 'var(--font-size-sm)', 
                            fontWeight: '500'
                          }}>
                            Balance: {formatCurrency(tx.balanceAfter)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Transaction Description */}
                      <div style={{
                        backgroundColor: 'var(--bg-secondary)',
                        padding: 'var(--spacing-sm)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-primary)',
                        fontSize: 'var(--font-size-sm)',
                        lineHeight: '1.4',
                        color: 'var(--text-primary)',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {tx.description || 'No description available'}
                      </div>
                      
                      {/* Additional metadata if available */}
                      {tx.metadata && (
                        <div style={{
                          marginTop: 'var(--spacing-xs)',
                          padding: 'var(--spacing-xs)',
                          backgroundColor: 'var(--bg-tertiary)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--border-primary)'
                        }}>
                          {tx.metadata.tournamentId && (
                            <div><strong className="proskins-text-primary">Tournament:</strong> {tx.metadata.tournamentId}</div>
                          )}
                          {tx.metadata.roundId && (
                            <div><strong className="proskins-text-primary">Round:</strong> {tx.metadata.roundId}</div>
                          )}
                          {tx.metadata.holeNumber && (
                            <div><strong className="proskins-text-primary">Hole:</strong> {tx.metadata.holeNumber}</div>
                          )}
                          {tx.metadata.golferName && (
                            <div><strong className="proskins-text-primary">Player:</strong> {tx.metadata.golferName}</div>
                          )}
                          {tx.metadata.prizeType && (
                            <div><strong className="proskins-text-primary">Prize Type:</strong> {tx.metadata.prizeType.replace('_', ' ')}</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {transactions.length > 0 && (
                  <div style={{
                    marginTop: 'var(--spacing-lg)',
                    padding: 'var(--spacing-md)',
                    backgroundColor: 'rgba(0, 255, 136, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    border: '1px solid rgba(0, 255, 136, 0.3)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacing-xs)' }}>
                      <span className="proskins-text-secondary">Total Credits:</span>
                      <strong style={{ color: 'var(--success)' }}>
                        +{formatCurrency(transactions
                          .filter(tx => ['DEPOSIT', 'WIN', 'REFUND', 'TRANSFER_IN'].includes(tx.type))
                          .reduce((sum, tx) => sum + parseFloat(tx.amount), 0)
                        )}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacing-xs)' }}>
                      <span className="proskins-text-secondary">Total Debits:</span>
                      <strong style={{ color: 'var(--error)' }}>
                        -{formatCurrency(transactions
                          .filter(tx => ['WITHDRAWAL', 'BET', 'TRANSFER_OUT'].includes(tx.type))
                          .reduce((sum, tx) => sum + parseFloat(tx.amount), 0)
                        )}
                      </strong>
                    </div>
                    <hr style={{ margin: 'var(--spacing-sm) 0', border: '1px solid var(--border-primary)' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-base)' }}>
                      <span className="proskins-text-primary"><strong>Net Balance Change:</strong></span>
                      <strong style={{ 
                        color: calculateTransactionTotal(transactions) >= 0 ? 'var(--success)' : 'var(--error)' 
                      }}>
                        {calculateTransactionTotal(transactions) >= 0 ? '+' : ''}
                        {formatCurrency(calculateTransactionTotal(transactions))}
                      </strong>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: 'var(--spacing-lg)' }}>
              <button
                className="proskins-btn proskins-btn-primary"
                onClick={() => setShowTransactionLogs(false)}
                style={{ padding: 'var(--spacing-sm) var(--spacing-lg)' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;